<template>
  <div class="content">
    <md-progress-bar
      v-if="loading"
      md-mode="indeterminate"
    />
    <div
      v-else
      class="md-layout"
    >
      <h3 class="md-headline h2">
        Update Product
      </h3>
      <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
        <!-- Product form -->
        <md-card>
          <md-card-content>
            <form class="node-form">
              <md-field :class="$v.product.sku.$invalid && 'md-invalid invalid-field'">
                <label>SKU</label>
                <md-input v-model="product.sku" />
                <span class="md-error">Field must not be empty</span>
              </md-field>

              <md-field :class="$v.product.index.$invalid && 'md-invalid invalid-field'">
                <label>Index</label>
                <md-input v-model="product.index" />
                <span class="md-error">Field must be an integer</span>
              </md-field>

              <md-field>
                <label>Type</label>
                <md-select v-model="product.product_type">
                  <md-option value="subscription">
                    Subscription
                  </md-option>
                  <md-option value="one_time_purchase">
                    One time purchase
                  </md-option>
                </md-select>
              </md-field>

              <md-field>
                <label>Version Filter Pattern</label>
                <md-input v-model="product.version_filter_pattern" />
                <span class="md-helper-text">Possible values: &lt;1.1.1 more than, >1.1.1 less than, 1.1.1 equal, empty - no filter</span>
              </md-field>

              <md-field :class="$v.product.credit_to_apply_on_wallet.$invalid && 'md-invalid invalid-field'">
                <label>Credit to apply on wallet</label>
                <md-input
                  v-model="product.credit_to_apply_on_wallet"
                  type="number"
                />
                <span class="md-error">Field must be an integer</span>
              </md-field>

              <md-switch v-model="product['enabled']">
                Enabled
              </md-switch>

              <div class="trial-field-line">
                <md-switch v-model="product.has_trial">
                  Has trial?
                </md-switch>
                <md-field :class="$v.product.trial_period_in_days.$invalid && 'md-invalid invalid-field'">
                  <label>Trial period, days</label>
                  <md-input
                    v-model="product.trial_period_in_days"
                    :disabled="!product.has_trial"
                  />
                  <span class="md-error">Field must be an integer</span>
                </md-field>
              </div>
            </form>

            <country-selector-with-continents
              :initial-mode="product['countries_access_type']"
              :initial-iso-codes="product['countries_iso_codes']"
              :loading="loading"
              :locked-option="'whitelist'"
              @countryListChanged="onCountriesListChanged"
              @modeChanged="onCountriesModeChanged"
            />
          </md-card-content>

          <md-card-actions>
            <md-button
              :disabled="$v.product.$invalid"
              type="submit"
              class="md-primary"
              @click.native="submitProductForm"
            >
              <span v-if="!loadingProductSubmission">Save</span>
              <md-progress-spinner
                v-else
                :md-diameter="15"
                :md-stroke="3"
                md-mode="indeterminate"
              />
            </md-button>
          </md-card-actions>
        </md-card>

        <!-- Stripe form -->
        <md-card>
          <md-card-content>
            <h3 class="h3">
              Stripe
            </h3>
            <md-progress-bar
              v-if="loadingStripeData"
              md-mode="indeterminate"
            />
            <form
              v-else
              class="node-form"
            >
              <div>
                <md-radio
                  v-model="stripe.productType"
                  value="subscription"
                  class="md-primary"
                >
                  Subscription
                </md-radio>
                <md-radio
                  v-model="stripe.productType"
                  value="one_time_purchase"
                  class="md-primary"
                >
                  One-time purchase
                </md-radio>
              </div>
              <md-field :class="$v.stripe.productId.$invalid && 'md-invalid invalid-field'">
                <label>Stripe Product ID</label>
                <md-input v-model="stripe.productId" />
                <span class="md-error">Must be {{
                  stripe.productType === 'subscription'
                    ? '"plan_..." or "price_..."'
                    : '"sku_"...'
                }}</span>
              </md-field>
              <md-field :class="$v.stripe.trialDays.$error && 'md-invalid invalid-field'">
                <label>Trial days</label>
                <md-input v-model="stripe.trialDays" />
                <span class="md-error">Must be a number</span>
              </md-field>
            </form>
          </md-card-content>

          <md-card-actions>
            <md-button
              type="submit"
              class="md-primary"
              @click.native="submitStripeForm"
            >
              <span v-if="!loadingStripeSubmission">Save</span>
              <md-progress-spinner
                v-else
                :md-diameter="15"
                :md-stroke="3"
                md-mode="indeterminate"
              />
            </md-button>
          </md-card-actions>
        </md-card>
        <!-- Chargebee forms -->
        <chargebee-form :product-id="id" />
        <!-- Platform payment forms -->
        <platform-payment-form :product-id="id" />


        <!-- Localizations' forms -->
        <h3 class="h3">
          Localizations
        </h3>
        <md-progress-bar
          v-if="loadingLocalizations"
          md-mode="indeterminate"
        />
        <!-- Bottom section with localizations' forms -->
        <div
          v-else
          class="localization-container"
        >
          <!-- Left half of the section with the default localization -->
          <div class="local-half default-column">
            <span class="md-title">Default</span>
            <md-card>
              <localization-form :localization="localizations.default" />
            </md-card>
          </div>

          <!-- Right half of the section with the custom localizations -->
          <div class="local-half">
            <!-- The plus sign button that adds an empty localization tab -->
            <md-button
              class="md-icon-button md-raised add-local-button md-primary"
              @click.native="addNewLocalization"
            >
              <md-icon>add</md-icon>
            </md-button>

            <md-tabs
              :md-active-tab="currentLocalizationTab"
              md-dynamic-height
              @md-changed="updateActiveTab"
            >
              <!--Overriding tab selectors, otherwise it becomes invisible-->
              <template
                slot="md-tab"
                slot-scope="{ tab }"
              >
                <span
                  class="tabs-header-item"
                  :class="{ 'selected-tab': tab.data.id === currentLocalizationTab }"
                >{{ tab.label }}</span>
              </template>

              <md-tab
                v-for="(local, index) in localizations.custom"
                :id="local.id || 'New'"
                :key="local.id || 'New'"
                :md-label="local.country || 'New'"
                :md-template-data="{ id: local.id || 'New' }"
              >
                <localization-form
                  :localization="local"
                  :index="index"
                  @deleteLocal="deleteLocalization"
                />
              </md-tab>
            </md-tabs>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
    import { LocalizationForm } from "@/components";
    import PlatformPaymentForm from "@/components/Forms/PlatformPaymentForm";
    import { required, integer, requiredIf } from "vuelidate/lib/validators";
    import ChargebeeForm from "@/components/Forms/ChargebeeForm";
    import CountrySelectorWithContinents from "@/components/Forms/Fields/CountrySelectorWithContinents";

    export default {
        components: {
            ChargebeeForm,
            LocalizationForm,
            PlatformPaymentForm,
            CountrySelectorWithContinents,
        },
        data() {
            return {
                id: this.$route.params.id,
                product: {},
                loading: true,
                loadingProductSubmission: false,

                stripe: {
                    productType: "subscription",
                    productId: "",
                    trialDays: ""
                },
                loadingStripeData: true,
                loadingStripeSubmission: false,
                isStripeIntegration: true,

                localizations: [],
                loadingLocalizations: true,
                currentLocalizationTab: "0",
            };
        },
        mounted() {
            this.fetchProduct();
            this.fetchLocalizations();
            this.fetchStripeData();
        },
        methods: {
            fetchProduct() {
                this.loading = true;
                this.$http.get(`/v1/admin/store/product/${this.id}`).then(result => {
                    this.product = result.data.product;
                    this.loading = false;
                }, err => {
                    console.error(err);
                });
            },
            fetchLocalizations() {
                this.loadingLocalizations = true;
                this.$http.get(`/v1/admin/store/product-localizations/${this.id}`).then(result => {
                    const locals = result.data["product_localizations"];
                    this.localizations = {
                        default: locals.find(l => l["default_localization"]),
                        custom: locals.filter(l => !l["default_localization"])
                    };
                    this.loadingLocalizations = false;
                    if (this.localizations.custom.length > 0) {
                        this.currentLocalizationTab = this.localizations.custom[0].id;
                    } else {
                        this.addNewLocalization();
                    }
                }, err => {
                    console.error(err);
                });
            },
            fetchStripeData() {
                this.loadingStripeData = true;
                this.$http.get(`/v1/admin/store/stripe-integration/${this.id}`).then(result => {
                    this.stripe = {
                        productType: result.data["product_type"],
                        productId: result.data["stripe_product_id"],
                        trialDays: result.data["trial_days"]
                    };
                    this.loadingStripeData = false;
                }, err => {
                    const { code } = err.response.data;
                    if (code === 5) {
                        this.loadingStripeData = false;
                        this.isStripeIntegration = false;
                    }
                });
            },
            submitProductForm () {
                this.loadingProductSubmission = true;
                this.$http.post("/v1/admin/store/product/update", {
                    id: this.product.id,
                    sku: this.product.sku,
                    enabled: this.product["enabled"],
                    index: this.product.index,
                    "countries_iso_codes": this.product["countries_iso_codes"],
                    "countries_access_type": this.product["countries_access_type"],
                    "product_type": this.product["product_type"],
                    has_trial: this.product.has_trial,
                    trial_period_in_days: this.product.trial_period_in_days,
                    version_filter_pattern: this.product.version_filter_pattern,
                    credit_to_apply_on_wallet: this.product.credit_to_apply_on_wallet
                })
                    .then(res => {
                        console.log(res);
                        this.loadingProductSubmission = false;
                    })
                    .catch(err => console.log(err));
            },
            submitStripeForm () {
                this.$v.stripe.$touch();
                if (this.$v.stripe.$error) {
                    return;
                }

                this.loadingStripeSubmission = true;
                this.$http[this.isStripeIntegration ? "put" : "post"](
                    "/v1/admin/store/stripe-integration",
                    {
                        product_id: this.product.id,
                        "product_type": this.stripe.productType,
                        "stripe_product_id": this.stripe.productId,
                        "trial_days": this.stripe.trialDays
                    }
                )
                    .then(res => {
                        console.log(res);
                        this.loadingStripeSubmission = false;
                    })
                    .catch(err => console.log(err));
            },
            updateActiveTab(tabId) {
                this.currentLocalizationTab = tabId;
            },
            addNewLocalization() {
                this.localizations.custom.push({
                    id: "",
                    product_id: this.product.id,
                    data: {
                        title: {},
                        subtitle: {},
                        cta: {},
                        discount: {}
                    },
                    benefits: [],
                    country: "",
                    default_localization: false,
                    duration: "",
                    currency: "",
                    amount: "",
                    name: ""
                });
                this.currentLocalizationTab = "New";
            },
            deleteLocalization(index) {
                this.localizations.custom.splice(index, 1);
                this.currentLocalizationTab = this.localizations.custom[0].id | "New";
            },
            onCountriesModeChanged(mode) {
                this.product["countries_access_type"] = mode;
            },
            onCountriesListChanged(newList) {
                this.product["countries_iso_codes"] = newList;
            }
        },
        validations: {
            product: {
                sku: { required },
                index: { required, integer },
                "countries_iso_codes": {
                    required: requiredIf(function () {
                        return this.product["countries_access_type"] !== "common";
                    })
                },
                trial_period_in_days: {
                    required: requiredIf(function () {
                        return this.product.has_trial;
                    }),
                    integer
                },
                credit_to_apply_on_wallet: { integer }
            },
            stripe: {
                productId: {
                    required,
                    validStripeProductId: function (value) {
                        return this.stripe.productType === "subscription"
                            ? value.startsWith("plan_") || value.startsWith("price_")
                            : value.startsWith("sku_");
                    }
                },
                productType: {required},
                trialDays: {required, integer}
            }
        }
    };
</script>
<style scoped lang="scss">
    .section-divider {
        margin-top: 25px;
        margin-bottom: 50px;
    }
    .tabs-header-item {
        color: black;
        padding: 5px;
        border-radius: 3px;
    }
    .selected-tab {
        background-color: #EEE;
    }
    .localization-container {
        width: 100%;
        display: flex;
        position: relative;
        div.local-half {
            width: 50%;
            padding: 0 3px;

            &.default-column {
                margin-top: 10px;
            }
        }

        .add-local-button {
            position: absolute;
            top: -25px;
            right: 10px;
            width: 40px;
            height: 40px;
            border-radius: 20px;
        }
    }

    .invalid-field {
        color: #ff1745;
        input, label {
            color: #ff1745 !important;
        }
    }
    .invalid-field:before {
        background-color: #ff1745 !important;
    }

    .trial-field-line {
      display: flex;

      & > div {
        width: 150px;
      }
    }
</style>
